import * as Y from "yjs";
import type {
  Cable,
  Circuit,
  Conductor,
  ConductorRouting,
  Device,
  DeviceOnSheet,
  ReferenceMark,
  Sheet,
  Terminal,
} from "./entities.ts";
import type {
  CableId,
  CircuitId,
  ConductorId,
  DeviceId,
  ReferenceId,
  SheetId,
  TerminalId,
} from "./ids.ts";

/**
 * LOCAL_ORIGIN markerar transaktioner som kommer från lokal UI-interaktion.
 * UseYjsReactFlow-hooken måste:
 *   - wrappa lokala mutationer med `yDoc.transact(fn, LOCAL_ORIGIN)`
 *   - observera updates och SKIPPA om `transaction.origin === LOCAL_ORIGIN`
 * Annars: feedback-loop → oändlig rerender.
 */
export const LOCAL_ORIGIN: symbol = Symbol("electro-planner.local");

export type DomainMaps = {
  devices: Y.Map<Y.Map<unknown>>;
  terminals: Y.Map<Y.Map<unknown>>;
  conductors: Y.Map<Y.Map<unknown>>;
  circuits: Y.Map<Y.Map<unknown>>;
  cables: Y.Map<Y.Map<unknown>>;
  sheets: Y.Map<Y.Map<unknown>>;
  deviceLayout: Y.Map<Y.Map<unknown>>;
  conductorRouting: Y.Map<Y.Map<unknown>>;
  references: Y.Map<Y.Map<unknown>>;
  sheetOrder: Y.Array<string>;
  // Platt key/value-map för projekt-metadata (projectName, projectNumber,
  // clientName, designer, companyName, revision). Alla värden är strängar så
  // att sync över wire inte kräver schema-handskakning för varje nytt fält.
  projectMeta: Y.Map<string>;
};

export type ProjectMeta = {
  projectName?: string;
  projectNumber?: string;
  clientName?: string;
  designer?: string;
  companyName?: string;
  revision?: string;
};

export const PROJECT_META_KEYS: ReadonlyArray<keyof ProjectMeta> = [
  "projectName",
  "projectNumber",
  "clientName",
  "designer",
  "companyName",
  "revision",
];

export function getDomainMaps(doc: Y.Doc): DomainMaps {
  return {
    devices: doc.getMap<Y.Map<unknown>>("devices"),
    terminals: doc.getMap<Y.Map<unknown>>("terminals"),
    conductors: doc.getMap<Y.Map<unknown>>("conductors"),
    circuits: doc.getMap<Y.Map<unknown>>("circuits"),
    cables: doc.getMap<Y.Map<unknown>>("cables"),
    sheets: doc.getMap<Y.Map<unknown>>("sheets"),
    deviceLayout: doc.getMap<Y.Map<unknown>>("deviceLayout"),
    conductorRouting: doc.getMap<Y.Map<unknown>>("conductorRouting"),
    references: doc.getMap<Y.Map<unknown>>("references"),
    sheetOrder: doc.getArray<string>("sheetOrder"),
    projectMeta: doc.getMap<string>("projectMeta"),
  };
}

export function getProjectMeta(maps: DomainMaps): ProjectMeta {
  const out: ProjectMeta = {};
  for (const key of PROJECT_META_KEYS) {
    const v = maps.projectMeta.get(key);
    if (typeof v === "string" && v.length > 0) out[key] = v;
  }
  return out;
}

export function setProjectMetaField(
  maps: DomainMaps,
  key: keyof ProjectMeta,
  value: string,
): void {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    maps.projectMeta.delete(key);
  } else {
    maps.projectMeta.set(key, trimmed);
  }
}

/**
 * Tömmer ALLA domänmaps — används innan Y.applyUpdate i ett restore-from-backup-
 * flöde. Ska köras inom en doc.transact() tillsammans med applyUpdate så att
 * rensning + restore blir ett atomärt undo-steg. Callers ansvarar för origin.
 */
export function clearDomainMaps(maps: DomainMaps): void {
  maps.devices.clear();
  maps.terminals.clear();
  maps.conductors.clear();
  maps.circuits.clear();
  maps.cables.clear();
  maps.sheets.clear();
  maps.deviceLayout.clear();
  maps.conductorRouting.clear();
  maps.references.clear();
  maps.projectMeta.clear();
  if (maps.sheetOrder.length > 0) {
    maps.sheetOrder.delete(0, maps.sheetOrder.length);
  }
}

export const layoutKey = (deviceId: DeviceId, sheetId: SheetId): string =>
  `${deviceId}:${sheetId}`;

export const routingKey = (conductorId: ConductorId, sheetId: SheetId): string =>
  `${conductorId}:${sheetId}`;

function toYMap<T extends object>(obj: T): Y.Map<unknown> {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(obj)) {
    m.set(k, v);
  }
  return m;
}

function fromYMap<T>(m: Y.Map<unknown>): T {
  return m.toJSON() as T;
}

// --- Device ---
export function putDevice(maps: DomainMaps, device: Device): void {
  maps.devices.set(device.id, toYMap(device));
}
export function getDevice(maps: DomainMaps, id: DeviceId): Device | undefined {
  const m = maps.devices.get(id);
  return m ? fromYMap<Device>(m) : undefined;
}
export function listDevices(maps: DomainMaps): Device[] {
  return Array.from(maps.devices.values(), (m) => fromYMap<Device>(m));
}

// --- Terminal ---
export function putTerminal(maps: DomainMaps, terminal: Terminal): void {
  maps.terminals.set(terminal.id, toYMap(terminal));
}
export function getTerminal(maps: DomainMaps, id: TerminalId): Terminal | undefined {
  const m = maps.terminals.get(id);
  return m ? fromYMap<Terminal>(m) : undefined;
}
export function listTerminals(maps: DomainMaps): Terminal[] {
  return Array.from(maps.terminals.values(), (m) => fromYMap<Terminal>(m));
}

// --- Conductor ---
export function putConductor(maps: DomainMaps, conductor: Conductor): void {
  maps.conductors.set(conductor.id, toYMap(conductor));
}
export function listConductors(maps: DomainMaps): Conductor[] {
  return Array.from(maps.conductors.values(), (m) => fromYMap<Conductor>(m));
}

// --- Cable ---
export function putCable(maps: DomainMaps, cable: Cable): void {
  maps.cables.set(cable.id, toYMap(cable));
}
export function getCable(maps: DomainMaps, id: CableId): Cable | undefined {
  const m = maps.cables.get(id);
  return m ? fromYMap<Cable>(m) : undefined;
}
export function listCables(maps: DomainMaps): Cable[] {
  return Array.from(maps.cables.values(), (m) => fromYMap<Cable>(m));
}
export function deleteCable(maps: DomainMaps, id: CableId): void {
  maps.cables.delete(id);
}
/**
 * Uppslagning conductor → cable. O(n*m) där n = kablar, m = conductors per kabel.
 * Bygg upp en gång och slå upp flera conductors i rad hellre än att anropa per
 * edge — projektionen använder detta varje re-render.
 */
export function buildConductorToCableIndex(
  maps: DomainMaps,
): Map<ConductorId, Cable> {
  const out = new Map<ConductorId, Cable>();
  for (const cable of listCables(maps)) {
    for (const cid of cable.conductors) {
      out.set(cid, cable);
    }
  }
  return out;
}
/**
 * Tar bort en conductor ur vilken kabel den än ligger i. Om kabeln blir tom
 * raderas den. Måste köras inom en runLocal-transaktion.
 */
export function removeConductorFromAnyCable(
  maps: DomainMaps,
  conductorIdToRemove: ConductorId,
): void {
  maps.cables.forEach((m, cid) => {
    const list = m.get("conductors");
    if (!Array.isArray(list)) return;
    if (!list.includes(conductorIdToRemove)) return;
    const next = list.filter((c) => c !== conductorIdToRemove);
    if (next.length === 0) {
      maps.cables.delete(cid);
    } else {
      m.set("conductors", next);
    }
  });
}

// --- Circuit ---
export function putCircuit(maps: DomainMaps, circuit: Circuit): void {
  maps.circuits.set(circuit.id, toYMap(circuit));
}
export function listCircuits(maps: DomainMaps): Circuit[] {
  return Array.from(maps.circuits.values(), (m) => fromYMap<Circuit>(m));
}

// --- Sheet ---
export function putSheet(maps: DomainMaps, sheet: Sheet): void {
  maps.sheets.set(sheet.id, toYMap(sheet));
}
export function getSheet(maps: DomainMaps, id: SheetId): Sheet | undefined {
  const m = maps.sheets.get(id);
  return m ? fromYMap<Sheet>(m) : undefined;
}
export function listSheets(maps: DomainMaps): Sheet[] {
  return Array.from(maps.sheets.values(), (m) => fromYMap<Sheet>(m));
}
/**
 * Returnerar sheets i visningsordning (enligt sheetOrder). Sheets som saknar
 * sheetOrder-entry placeras sist — försvarar mot inkonsistent state men ska inte
 * hända i praktiken eftersom mutations alltid synkar båda.
 */
export function listSheetsInOrder(maps: DomainMaps): Sheet[] {
  const byId = new Map<string, Sheet>();
  maps.sheets.forEach((m, id) => {
    byId.set(id, fromYMap<Sheet>(m));
  });
  const seen = new Set<string>();
  const out: Sheet[] = [];
  for (const id of maps.sheetOrder.toArray()) {
    const s = byId.get(id);
    if (s && !seen.has(id)) {
      out.push(s);
      seen.add(id);
    }
  }
  for (const [id, s] of byId) {
    if (!seen.has(id)) out.push(s);
  }
  return out;
}
export function getSheetIndex(maps: DomainMaps, id: SheetId): number {
  return maps.sheetOrder.toArray().indexOf(id);
}
/**
 * Tar bort sheet-entry + pekare i sheetOrder. Tar INTE bort deviceLayout/routing —
 * det ansvarar anroparen för (typiskt useDomainMutations.deleteSheet som kan
 * fatta beslut om cascade till devices).
 */
export function deleteSheet(maps: DomainMaps, id: SheetId): void {
  maps.sheets.delete(id);
  const arr = maps.sheetOrder.toArray();
  const idx = arr.indexOf(id);
  if (idx >= 0) maps.sheetOrder.delete(idx, 1);
}
/**
 * Flyttar sheet till newIndex i sheetOrder. Clampar utanför-index till giltigt
 * intervall. No-op om sheet inte finns eller om target == current.
 */
export function moveSheetInOrder(
  maps: DomainMaps,
  id: SheetId,
  newIndex: number,
): void {
  const arr = maps.sheetOrder.toArray();
  const current = arr.indexOf(id);
  if (current < 0) return;
  const clamped = Math.max(0, Math.min(arr.length - 1, newIndex));
  if (clamped === current) return;
  maps.sheetOrder.delete(current, 1);
  maps.sheetOrder.insert(clamped, [id]);
}

// --- DeviceOnSheet ---
export function putDeviceLayout(maps: DomainMaps, layout: DeviceOnSheet): void {
  maps.deviceLayout.set(layoutKey(layout.deviceId, layout.sheetId), toYMap(layout));
}
export function getDeviceLayout(
  maps: DomainMaps,
  deviceId: DeviceId,
  sheetId: SheetId,
): DeviceOnSheet | undefined {
  const m = maps.deviceLayout.get(layoutKey(deviceId, sheetId));
  return m ? fromYMap<DeviceOnSheet>(m) : undefined;
}
export function listDeviceLayoutsForSheet(
  maps: DomainMaps,
  sheetId: SheetId,
): DeviceOnSheet[] {
  const out: DeviceOnSheet[] = [];
  maps.deviceLayout.forEach((m, key) => {
    if (key.endsWith(`:${sheetId}`)) out.push(fromYMap<DeviceOnSheet>(m));
  });
  return out;
}
export function listAllDeviceLayouts(maps: DomainMaps): DeviceOnSheet[] {
  return Array.from(maps.deviceLayout.values(), (m) => fromYMap<DeviceOnSheet>(m));
}
export function deleteDeviceLayout(
  maps: DomainMaps,
  deviceId: DeviceId,
  sheetId: SheetId,
): void {
  maps.deviceLayout.delete(layoutKey(deviceId, sheetId));
}
/**
 * Returnerar alla sheetIds som en device har layout på. Används för att
 * avgöra om en device är "föräldralös" efter att ett sheet tas bort.
 */
export function listSheetsForDevice(
  maps: DomainMaps,
  deviceId: DeviceId,
): SheetId[] {
  const out: SheetId[] = [];
  const prefix = `${deviceId}:`;
  maps.deviceLayout.forEach((_, key) => {
    if (key.startsWith(prefix)) {
      const rest = key.slice(prefix.length);
      out.push(rest as SheetId);
    }
  });
  return out;
}

// --- ConductorRouting ---
export function putConductorRouting(maps: DomainMaps, routing: ConductorRouting): void {
  maps.conductorRouting.set(
    routingKey(routing.conductorId, routing.sheetId),
    toYMap(routing),
  );
}
export function listRoutingsForSheet(
  maps: DomainMaps,
  sheetId: SheetId,
): ConductorRouting[] {
  const out: ConductorRouting[] = [];
  maps.conductorRouting.forEach((m, key) => {
    if (key.endsWith(`:${sheetId}`)) out.push(fromYMap<ConductorRouting>(m));
  });
  return out;
}
export function deleteRoutingsForSheet(maps: DomainMaps, sheetId: SheetId): void {
  const keysToDelete: string[] = [];
  maps.conductorRouting.forEach((_, key) => {
    if (key.endsWith(`:${sheetId}`)) keysToDelete.push(key);
  });
  for (const k of keysToDelete) maps.conductorRouting.delete(k);
}
export function deleteLayoutsForSheet(maps: DomainMaps, sheetId: SheetId): void {
  const keysToDelete: string[] = [];
  maps.deviceLayout.forEach((_, key) => {
    if (key.endsWith(`:${sheetId}`)) keysToDelete.push(key);
  });
  for (const k of keysToDelete) maps.deviceLayout.delete(k);
}

// --- ReferenceMark ---
export function putReferenceMark(maps: DomainMaps, ref: ReferenceMark): void {
  maps.references.set(ref.id, toYMap(ref));
}
export function listReferenceMarks(maps: DomainMaps): ReferenceMark[] {
  return Array.from(maps.references.values(), (m) => fromYMap<ReferenceMark>(m));
}

/**
 * Applicerar en mutation-funktion inom en Yjs-transaktion med LOCAL_ORIGIN.
 * Använd detta för ALLA lokala UI-mutationer.
 */
export function runLocal<T>(doc: Y.Doc, fn: () => T): T {
  let result!: T;
  doc.transact(() => {
    result = fn();
  }, LOCAL_ORIGIN);
  return result;
}

// Re-export Y för konsumenter som behöver typnivå-åtkomst.
export { Y };

// Dessa är bara för typ-referens; undertrycker unused-typ-warnings.
export type { CableId, CircuitId, ConductorId, DeviceId, ReferenceId, SheetId, TerminalId };
